import { setPubsubStream } from './../apiHelper';
import { concat, Observable, Subscriber, of, zip } from "rxjs";
import { share, map, scan, retryWhen, takeUntil, finalize, throttleTime } from "rxjs/operators";
import { CDSData, CDSObserverModel, CDSParsedData, ProcessType } from "../../types";
import { errorHandler } from "../apiHelper";
import lodashMerge from 'lodash/merge';
import { getCDSInitialData } from "../ALCInitialDataServices/ALCInitialDataServices";
import { getFactoryChangeObservable } from '../../Auth';
import { withLogger } from '../LogerServices';

const CDSType = ProcessType.CDS;

const CDSObserver: CDSObserverModel = {
    cdsObservable: null as Observable<CDSParsedData>,
}

const groupCDSParams = (data: CDSData) => {
    return Object.entries(data).reduce((acc, [param, value]) => {
        const ignoredParam = 'HistorianDrumSpeedFor';
        if (param.indexOf(ignoredParam) === 0) {
            return acc;
        }
        const [solution, parameters] = param.split(" ");
        const values = Object.assign({}, acc.values,
            {
                [solution]: Object.assign({},
                    acc.values[solution],
                    { [parameters]: value }
                )
            })
        return Object.assign({}, acc, { values });
    }, { values: {} } as CDSParsedData);
}

const parseCDSData = (data: CDSData): CDSParsedData => {
    const parseStringToBoolean = (value: string) => value === 'true' ? true : false;
    const { IsBreakdown, IsEnableLine2, PhaseAsText, IsNopCalculated, IsConvBeltSpeedCalculated, ...rest } = data;
    const whitelistedElements = (({ FtdInletOvHistory, OutCutterStartEnable, ...o }) => o)(rest);
    const groupedValues = groupCDSParams(whitelistedElements);

    return {
        ...groupedValues,
        ...(IsEnableLine2) && { IsEnableLine2: parseStringToBoolean(IsEnableLine2 as string) },
        ...(IsBreakdown) && { IsBreakdown: parseStringToBoolean(IsBreakdown as string) },
        ...(IsNopCalculated) && { IsNopCalculated: parseStringToBoolean(IsNopCalculated as string) },
        ...(IsConvBeltSpeedCalculated) && { IsConvBeltSpeedCalculated: parseStringToBoolean(IsConvBeltSpeedCalculated as string) },
        PhaseAsText
    } as CDSParsedData
};

const setCDSStream = () => new Observable<CDSData>((observer: Subscriber<CDSData>) => {
    const close = setPubsubStream(observer, CDSType)
    return close;
}).pipe(
    retryWhen(errors => errorHandler(errors)),
    takeUntil(getFactoryChangeObservable()),
    share()
);

const setDefaultParams = () => of({
    'PhaseAsText': '',
    'Conveyor_speed SP': '',
    'Conveyor_speed PV': '',
    'SRB_tobacco_level SP': '',
    'SRB_tobacco_level PV': '',
    'Drum_speed SP': '',
    'Drum_speed PV': '',
    'Drum_speed Mouth': '',
    'InletOV Cutter_hist': '',
    'InletOV Cutter_exit': '',
    'FTD_tobacco_level PV': '',
    'FTD_tobacco_flow NOP_historian': '',
    'FTD_tobacco_flow NOP_corrected': '',
    'FTD_tobacco_flow Actual_flow': '',
    'IsConvBeltSpeedCalculated': 'false',
    'IsNopCalculated': 'false',
    'IsBreakdown': 'false',
    'IsEnableLine2': 'false',
    'OutCutterStartEnable': 'false'
})

export const getCDSInitialDataWithDefaultParams = () => zip(
    setDefaultParams(),
    getCDSInitialData(),
).pipe(
    map(([defaultData, initialData]) => lodashMerge({}, defaultData, initialData)
    ));

const CDSDataObservable = () => {
    CDSObserver.cdsObservable = concat(
        withLogger(getCDSInitialDataWithDefaultParams(), CDSType, {isInitial: true}),
        withLogger(setCDSStream(), CDSType)
    ).pipe(
        map(parseCDSData),
        scan((acc, val) => lodashMerge({}, acc, val), {}),
        throttleTime(1000),
        finalize(() => CDSObserver.cdsObservable = null),
        share()
    );
    return CDSObserver.cdsObservable
}

export const getCDSDataObservable = (): Observable<CDSParsedData> => CDSObserver.cdsObservable ? CDSObserver.cdsObservable : CDSDataObservable();